<template>
  <div class="churn-analysis-wrapper" v-loading.fullscreen.lock="loading">
    <!-- 游戏选择（编辑从看板下钻进入、已锁定书，不展示选择器） -->
    <div class="game-selector" v-if="!isEditor">
      <el-select v-model="currentGameId" placeholder="选择游戏" @change="onGameSelect" filterable style="width: 300px;">
        <el-option v-for="g in allGames" :key="g.id" :label="g.title" :value="g.id" />
      </el-select>
    </div>

    <!-- 筛选区域 -->
    <div class="filter-section">
      <el-card shadow="hover">
        <div class="filter-content">
          <!-- 注册日期范围 -->
          <div class="filter-item" v-if="!isEditor">
            <label class="filter-label">注册开始日期：</label>
            <el-date-picker v-model="filterStartTime" type="date" placeholder="选择注册开始日期" format="yyyy-MM-dd" value-format="yyyy-MM-dd" style="width: 180px;" />
          </div>
          <div class="filter-item" v-if="!isEditor">
            <label class="filter-label">注册结束日期：</label>
            <el-date-picker v-model="filterEndTime" type="date" placeholder="选择注册结束日期" format="yyyy-MM-dd" value-format="yyyy-MM-dd" style="width: 180px;" />
          </div>

          <!-- N 日内窗口 -->
          <div class="filter-item" v-if="!isEditor">
            <label class="filter-label">N 日内：</label>
            <el-select v-model="filterNDays" style="width: 110px;">
              <el-option :value="0" label="0日内" />
              <el-option :value="1" label="1日内" />
              <el-option :value="3" label="3日内" />
              <el-option :value="7" label="7日内" />
              <el-option :value="14" label="14日内" />
              <el-option :value="30" label="30日内" />
            </el-select>
          </div>

          <!-- 节点名称搜索 -->
          <div class="filter-item" v-if="!isEditor">
            <label class="filter-label">节点名称：</label>
            <el-input
              v-model="filterNodeName"
              placeholder="请输入节点名称"
              clearable
              style="width: 200px;"
              @clear="handleSearch"
              @keyup.enter.native="handleSearch"
            />
          </div>

          <!-- 节点类型筛选 -->
          <div class="filter-item" v-if="!isEditor">
            <label class="filter-label">节点类型：</label>
            <el-radio-group v-model="filterNodeType">
              <el-radio-button label="ALL">全部</el-radio-button>
              <el-radio-button label="AI">AI</el-radio-button>
              <el-radio-button label="固定">固定</el-radio-button>
            </el-radio-group>
          </div>

          <!-- 警戒值输入 -->
          <div class="filter-item">
            <label class="filter-label">流失率警戒值：</label>
            <el-input-number
              v-model="filterThreshold"
              :min="0"
              :max="1"
              :step="0.01"
              :precision="2"
              style="width: 150px;"
            />
          </div>

          <!-- 设备来源筛选 -->
          <div class="filter-item" v-if="!isEditor">
            <label class="filter-label">设备来源：</label>
            <el-select
              v-model="filterChannels"
              multiple
              collapse-tags
              placeholder="请选择设备来源"
              style="width: 220px;"
              @change="handleChannelChange"
            >
              <el-option label="全部" value="all" />
              <el-option label="头条" value="toutiao" />
              <el-option label="快手" value="kuaishou" />
              <el-option label="其他" value="other" />
            </el-select>
          </div>

          <!-- 聚合固定剧情开关 -->
          <div class="filter-item">
            <el-checkbox v-model="aggregateFixed">聚合固定剧情</el-checkbox>
          </div>

          <!-- 查询按钮 -->
          <el-button type="primary" icon="el-icon-search" @click="handleSearch">查询</el-button>
          <el-button icon="el-icon-refresh" @click="handleReset">重置</el-button>
        </div>
      </el-card>
    </div>

    <!-- 结果表格 -->
    <div class="result-section">
      <el-table
        :data="churnData"
        style="width: 100%"
        border
        :row-class-name="tableRowClassName"
      >
        <el-table-column prop="node_order" label="序号" width="80" sortable />
        <el-table-column label="节点名称" min-width="250" show-overflow-tooltip>
          <template slot-scope="scope">
            <span>{{ scope.row.node_name }}</span>
            <span v-if="scope.row.node_type === '固定' && scope.row.dlg_count != null" class="dlg-count-tip">共 {{ scope.row.dlg_count }} 条对白</span>
          </template>
        </el-table-column>
        <el-table-column prop="node_id" label="节点ID" width="100" sortable />
        <el-table-column prop="node_type" label="类型" width="80">
          <template slot-scope="scope">
            <el-tag :type="scope.row.node_type === 'AI' ? 'primary' : 'info'" size="small">
              {{ scope.row.node_type }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="enter_count" label="进入人数" width="100" sortable />
        <el-table-column prop="churn_count" label="流失人数" width="100" sortable />
        <el-table-column prop="churn_rate" label="流失率" width="120" sortable>
          <template slot-scope="scope">
            <span :class="{ 'warning-rate': scope.row.is_warning }">
              {{ (scope.row.churn_rate * 100).toFixed(2) }}%
            </span>
            <el-tag v-if="scope.row.is_warning" type="danger" size="mini" style="margin-left: 5px;">
              警告
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column label="没发消息流失占比" width="150">
          <template slot-scope="scope">
            <span v-if="scope.row.node_type === 'AI'">
              {{ (scope.row.no_msg_churn_ratio * 100).toFixed(2) }}%
            </span>
            <span v-else class="na-text">-</span>
          </template>
        </el-table-column>
        <el-table-column label="发了消息流失占比" width="150">
          <template slot-scope="scope">
            <span v-if="scope.row.node_type === 'AI'">
              {{ (scope.row.with_msg_churn_ratio * 100).toFixed(2) }}%
            </span>
            <span v-else class="na-text">-</span>
          </template>
        </el-table-column>
        <el-table-column label="操作" width="100" fixed="right">
          <template slot-scope="scope">
            <el-button
              type="text"
              size="small"
              @click="handleViewUsers(scope.row)"
            >
              查看用户
            </el-button>
          </template>
        </el-table-column>
      </el-table>

      <!-- 空状态 -->
      <div v-if="!loading && churnData.length === 0" style="text-align: center; padding: 40px; color: #999;">
        <i class="el-icon-data-analysis" style="font-size: 64px; margin-bottom: 20px;"></i>
        <p>暂无流失数据</p>
      </div>
    </div>

    <!-- 统计摘要 -->
    <div v-if="churnData.length > 0" class="summary-section">
      <el-card shadow="hover">
        <div slot="header" class="summary-header">
          <span>统计摘要</span>
        </div>
        <div class="summary-content">
          <div class="summary-item">
            <span class="summary-label">总节点数：</span>
            <span class="summary-value">{{ churnData.length }}</span>
          </div>
          <div class="summary-item">
            <span class="summary-label">警告节点数：</span>
            <span class="summary-value warning-count">{{ warningCount }}</span>
          </div>
          <div class="summary-item">
            <span class="summary-label">平均流失率：</span>
            <span class="summary-value">{{ (averageChurnRate * 100).toFixed(2) }}%</span>
          </div>
          <div class="summary-item">
            <span class="summary-label">最高流失率节点：</span>
            <span class="summary-value">{{ maxChurnNode }}</span>
          </div>
        </div>
      </el-card>
    </div>

    <!-- 章节耗时统计 -->
    <div v-if="chapterTimeStats.length > 0" class="chapter-time-section">
      <el-card shadow="hover">
        <div slot="header" class="chapter-time-header">
          <span class="chapter-time-title">章节耗时统计（分钟）</span>
          <span class="chapter-time-tip">耗时 = 进入下一章首节点时间 − 进入本章首节点时间，取完成该章用户的中位数；累积耗时为各章耗时累加</span>
        </div>
        <div class="chapter-time-wrap">
          <table class="chapter-time-table">
            <thead>
              <tr>
                <th class="ct-rowhead">统计类型</th>
                <th v-for="c in chapterTimeStats" :key="c.chapter_order">第{{ c.chapter_order }}章</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td class="ct-rowhead">完成人数</td>
                <td v-for="c in chapterTimeStats" :key="c.chapter_order">{{ c.completed_count > 0 ? c.completed_count : '-' }}</td>
              </tr>
              <tr>
                <td class="ct-rowhead">耗时</td>
                <td v-for="c in chapterTimeStats" :key="c.chapter_order">{{ c.duration_minutes != null ? c.duration_minutes : '-' }}</td>
              </tr>
              <tr>
                <td class="ct-rowhead">累积耗时</td>
                <td v-for="c in chapterTimeStats" :key="c.chapter_order">{{ c.cumulative_minutes > 0 ? c.cumulative_minutes : '-' }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </el-card>
    </div>

    <!-- 流失用户对话记录弹窗 -->
    <el-dialog
      :title="dialogTitle"
      :visible.sync="dialogVisible"
      width="90%"
      top="3vh"
      class="churn-users-dialog"
    >
      <div v-loading="dialogLoading" class="dialog-content">
        <!-- 只有「流失」和「没有流失」都为空（= 该节点根本没人进入）才算无数据。
             不能只看流失用户：本弹窗右栏展示的是没有流失的用户的对话记录，
             流失数为 0 的节点照样有内容可看，早先按流失数判空会把整个三栏一起藏掉。 -->
        <div
          v-if="!dialogLoading && churnUsersData.length === 0 && retainedTotal === 0"
          class="no-data"
        >
          <i class="el-icon-document"></i>
          <p>该节点在当前筛选条件下没有用户进入，无对话记录</p>
        </div>

        <!-- 三栏布局 -->
        <div v-else class="three-column-layout">
          <!-- 左栏：没发消息的用户 -->
          <div class="column left-column">
            <div class="column-header">
              <span class="column-title">没发消息直接流失</span>
              <span class="column-count">（{{ noMsgUsers.length }} 人）</span>
            </div>
            <div class="column-content">
              <div
                v-for="user in noMsgUsers"
                :key="user.device_id"
                class="user-chat-block"
              >
                <div class="user-header">
                  <div class="user-info">
                    <span class="user-id">user_id: {{ user.user_id || '-' }} · record_id: {{ user.record_id || '-' }} · node_id: {{ user.node_id || '-' }}</span>
                    <span class="user-name">{{ user.username || '未知用户' }}</span>
                  </div>
                  <div class="user-max-node" :title="user.max_node_name">
                    最大节点: {{ user.max_node_name || '-' }}
                  </div>
                </div>
                <div class="chat-messages">
                  <div
                    v-for="(msg, msgIndex) in user.messages"
                    :key="msgIndex"
                    :class="['message-item', msg.is_user ? 'user-message' : 'ai-message']"
                  >
                    <div class="message-avatar">
                      <img v-if="msg.avatar_url" :src="msg.avatar_url" :alt="msg.speaker_name" />
                      <div v-else class="default-avatar">{{ msg.speaker_name ? msg.speaker_name.charAt(0) : '?' }}</div>
                    </div>
                    <div class="message-content">
                      <div class="message-header">
                        <span class="speaker-name">{{ msg.speaker_name }}</span>
                        <span v-if="msg.send_time" class="send-time">{{ msg.send_time }}</span>
                      </div>
                      <div class="message-text">{{ msg.text }}</div>
                    </div>
                  </div>
                  <div v-if="user.messages.length === 0" class="no-messages">
                    无对话记录
                  </div>
                </div>
              </div>
              <div v-if="noMsgUsers.length === 0" class="empty-column">
                暂无数据
              </div>
            </div>
          </div>

          <!-- 中栏：发了消息的用户 -->
          <div class="column middle-column">
            <div class="column-header">
              <span class="column-title">发了消息后流失</span>
              <span class="column-count">（{{ withMsgUsers.length }} 人）</span>
            </div>
            <div class="column-content">
              <div
                v-for="user in withMsgUsers"
                :key="user.device_id"
                class="user-chat-block"
              >
                <div class="user-header">
                  <div class="user-info">
                    <span class="user-id">user_id: {{ user.user_id || '-' }} · record_id: {{ user.record_id || '-' }} · node_id: {{ user.node_id || '-' }}</span>
                    <span class="user-name">{{ user.username || '未知用户' }}</span>
                  </div>
                  <div class="user-max-node" :title="user.max_node_name">
                    最大节点: {{ user.max_node_name || '-' }}
                  </div>
                </div>
                <div class="chat-messages">
                  <div
                    v-for="(msg, msgIndex) in user.messages"
                    :key="msgIndex"
                    :class="['message-item', msg.is_user ? 'user-message' : 'ai-message']"
                  >
                    <div class="message-avatar">
                      <img v-if="msg.avatar_url" :src="msg.avatar_url" :alt="msg.speaker_name" />
                      <div v-else class="default-avatar">{{ msg.speaker_name ? msg.speaker_name.charAt(0) : '?' }}</div>
                    </div>
                    <div class="message-content">
                      <div class="message-header">
                        <span class="speaker-name">{{ msg.speaker_name }}</span>
                        <span v-if="msg.send_time" class="send-time">{{ msg.send_time }}</span>
                      </div>
                      <div class="message-text">{{ msg.text }}</div>
                    </div>
                  </div>
                </div>
              </div>
              <div v-if="withMsgUsers.length === 0" class="empty-column">
                暂无数据
              </div>
            </div>
          </div>

          <!-- 右栏：没有流失的用户 -->
          <div class="column right-column retained-column">
            <div class="column-header">
              <span class="column-title">没有流失</span>
              <span class="column-count">（共 {{ retainedTotal }} 人）</span>
            </div>
            <div class="column-content">
              <div
                v-for="user in retainedUsers"
                :key="user.device_id"
                class="user-chat-block"
              >
                <div class="user-header">
                  <div class="user-info">
                    <span class="user-id">user_id: {{ user.user_id || '-' }} · record_id: {{ user.record_id || '-' }} · node_id: {{ user.node_id || '-' }}</span>
                    <span class="user-name">{{ user.username || '未知用户' }}</span>
                  </div>
                  <div class="user-max-node" :title="user.max_node_name">
                    最大节点: {{ user.max_node_name || '-' }}
                  </div>
                </div>
                <div class="chat-messages">
                  <div
                    v-for="(msg, msgIndex) in user.messages"
                    :key="msgIndex"
                    :class="['message-item', msg.is_user ? 'user-message' : 'ai-message']"
                  >
                    <div class="message-avatar">
                      <img v-if="msg.avatar_url" :src="msg.avatar_url" :alt="msg.speaker_name" />
                      <div v-else class="default-avatar">{{ msg.speaker_name ? msg.speaker_name.charAt(0) : '?' }}</div>
                    </div>
                    <div class="message-content">
                      <div class="message-header">
                        <span class="speaker-name">{{ msg.speaker_name }}</span>
                        <span v-if="msg.send_time" class="send-time">{{ msg.send_time }}</span>
                      </div>
                      <div class="message-text">{{ msg.text }}</div>
                    </div>
                  </div>
                </div>
              </div>
              <div v-if="retainedUsers.length === 0" class="empty-column">
                暂无数据
              </div>
            </div>
            <!-- 分页控件 -->
            <div class="pagination-wrapper" v-if="retainedTotal > retainedPageSize">
              <el-pagination
                small
                layout="prev, pager, next"
                :total="retainedTotal"
                :page-size="retainedPageSize"
                :current-page="retainedPage"
                @current-change="handleRetainedPageChange"
              />
            </div>
          </div>
        </div>
      </div>
    </el-dialog>
  </div>
</template>

<script lang="ts">
import { Component, Prop, Vue, Watch } from 'vue-property-decorator'
import service from '@/utils/request'
import { Game, getGames } from '@/api/game'
import { getChapterTimeStats, ChapterTimeStat, ChapterTimeStatsParams } from '@/api/chapterTimeStats'

interface ChurnItem {
  node_id: number
  group_node_ids?: number[]
  node_name: string
  node_order: number
  node_type: string
  enter_count: number
  churn_count: number
  churn_rate: number
  no_msg_churn_ratio: number
  with_msg_churn_ratio: number
  is_warning: boolean
  dlg_count?: number | null  // 非聚合模式：该节点对白条数（固定节点用于展示"共x条对白"）
}

interface ChatMessage {
  speaker_name: string
  avatar_url: string
  text: string
  is_user: boolean
  send_time: string
}

interface ChurnUserData {
  device_id: string
  user_id: number | null
  record_id: number | null
  node_id: number | null
  username: string
  max_node_name: string
  has_user_message: boolean
  messages: ChatMessage[]
}

@Component
export default class ChurnAnalysis extends Vue {
  @Prop({ required: true }) readonly game!: Game

  allGames: Game[] = []
  currentGameId: number | null = null

  churnData: ChurnItem[] = []
  chapterTimeStats: ChapterTimeStat[] = []  // 章节耗时统计
  loading = false

  // 按浏览器本地日期取截至昨天的最近 7 个自然日，与其他数据页一致。
  filterStartTime = ''
  filterEndTime = ''
  filterNDays = 0
  filterNodeName = ''
  filterNodeType = 'AI'  // 默认选择 AI
  filterThreshold = 0.05  // 默认值改为 0.05
  filterChannels: string[] = ['all']  // 默认选择全部
  aggregateFixed = false  // 聚合固定剧情，默认不勾选

  // 弹窗相关
  dialogVisible = false
  dialogLoading = false
  dialogTitle = ''
  currentNode: ChurnItem | null = null
  churnUsersData: ChurnUserData[] = []

  // 没流失用户相关
  retainedUsers: ChurnUserData[] = []
  retainedTotal = 0
  retainedPage = 1
  retainedPageSize = 10

  // 当前用户是否编辑角色（编辑：仅看板下钻进入，隐藏部分筛选项）
  get isEditor(): boolean {
    return this.$store.state.user.user?.role === 'editor'
  }

  // 计算属性：没发消息的用户
  get noMsgUsers(): ChurnUserData[] {
    return this.churnUsersData.filter(u => !u.has_user_message)
  }

  // 计算属性：发了消息的用户
  get withMsgUsers(): ChurnUserData[] {
    return this.churnUsersData.filter(u => u.has_user_message)
  }

  get selectedGame(): Game | null {
    if (this.currentGameId) {
      const found = this.allGames.find(g => g.id === this.currentGameId)
      if (found) return found
    }
    return this.game || null
  }

  created() {
    this.applyDefaultDates()
  }

  @Watch('game', { immediate: true })
  async onGameChange(newGame: Game) {
    if (newGame && newGame.id) {
      this.currentGameId = newGame.id
    }
  }

  async mounted() {
    await this.loadAllGames()
    // 编辑角色：节点类型筛选器已隐藏，查询值固定为「全部」
    if (this.isEditor) {
      this.filterNodeType = 'ALL'
    }
    // 从分书数据 / 分书新增数据页跳转而来：?game_id=xxx，预选该书并自动查询（含节点流失表与章节耗时表）
    const qid = this.$route.query.game_id
    if (qid) {
      this.currentGameId = Number(Array.isArray(qid) ? qid[0] : qid)
      // 日期范围沿用来源页的统计起止日期；未传入的其他参数沿用默认值。
      const qs = this.$route.query.start_date
      const qe = this.$route.query.end_date
      const qn = this.$route.query.n_days
      if (qs) this.filterStartTime = String(Array.isArray(qs) ? qs[0] : qs)
      if (qe) this.filterEndTime = String(Array.isArray(qe) ? qe[0] : qe)
      if (qn !== undefined) this.filterNDays = Number(Array.isArray(qn) ? qn[0] : qn)
      this.handleSearch()
    }
  }

  async loadAllGames() {
    try {
      this.allGames = await getGames()
    } catch {
      // ignore
    }
  }

  onGameSelect(gameId: number) {
    this.currentGameId = gameId
    this.churnData = []
  }

  async loadChurnData() {
    if (!this.currentGameId) {
      this.$message.warning('请先选择游戏')
      return
    }

    this.loading = true
    try {
      const params: any = {
        game_id: this.currentGameId,
        threshold: this.filterThreshold,
        start_date: this.filterStartTime,
        end_date: this.filterEndTime,
        n_days: this.filterNDays
      }

      if (this.filterNodeName) {
        params.node_name = this.filterNodeName
      }

      if (this.filterNodeType !== 'ALL') {
        params.node_type = this.filterNodeType
      }

      // 设备来源过滤（如果不是"全部"则传递channels参数）
      if (!this.filterChannels.includes('all')) {
        params.channels = this.filterChannels.join(',')
      }

      // 聚合固定剧情
      if (this.aggregateFixed) {
        params.aggregate_fixed = '1'
      }

      const response = await service.get('/api/churn_analysis', { params })
      if (response.data.message === 'success') {
        this.churnData = response.data.data
      }
    } catch (error) {
      console.error('获取流失数据失败:', error)
      this.$message.error('获取流失数据失败')
    } finally {
      this.loading = false
    }
  }

  async loadChapterTimeStats() {
    if (!this.currentGameId) return
    try {
      const params: ChapterTimeStatsParams = {
        game_id: this.currentGameId,
        start_date: this.filterStartTime,
        end_date: this.filterEndTime,
        n_days: this.filterNDays
      }
      // 设备来源过滤（与节点流失表同口径；"全部"时不传）
      if (!this.filterChannels.includes('all')) {
        params.channels = this.filterChannels.join(',')
      }
      this.chapterTimeStats = await getChapterTimeStats(params)
    } catch (error) {
      console.error('获取章节耗时统计失败:', error)
      this.chapterTimeStats = []
    }
  }

  handleSearch() {
    this.loadChurnData()
    this.loadChapterTimeStats()
  }

  handleReset() {
    if (this.isEditor) {
      // 编辑：保留下钻带来的日期范围/N日内，仅重置可见控件
      this.filterNodeType = 'ALL'
      this.filterThreshold = 0.05
      this.filterChannels = ['all']
      this.aggregateFixed = false
      this.churnData = []
      this.chapterTimeStats = []
      return
    }
    this.applyDefaultDates()
    this.filterNDays = 0
    this.filterNodeName = ''
    this.filterNodeType = 'AI'  // 重置为 AI
    this.filterThreshold = 0.05  // 重置为 0.05
    this.filterChannels = ['all']  // 重置为全部
    this.aggregateFixed = false  // 重置为不聚合
    this.churnData = []  // 重置时清空数据，不自动查询
    this.chapterTimeStats = []
  }

  handleChannelChange(val: string[]) {
    // 互斥逻辑：选择"全部"则清除其他选项，选择其他选项则清除"全部"
    if (val.length === 0) {
      // 如果清空了所有选择，默认选择"全部"
      this.filterChannels = ['all']
    } else if (val.includes('all') && val.length > 1) {
      // 如果新选择包含"全部"且有其他选项
      if (val[val.length - 1] === 'all') {
        // 用户刚选择了"全部"，清除其他选项
        this.filterChannels = ['all']
      } else {
        // 用户选择了其他选项，清除"全部"
        this.filterChannels = val.filter(v => v !== 'all')
      }
    }
  }

  // 每次打开 / 重置都重新读取本地日期；看板下钻传入的日期在 mounted 中覆盖。
  applyDefaultDates() {
    const today = new Date()
    const startD = new Date(today)
    startD.setDate(startD.getDate() - 7)
    const endD = new Date(today)
    endD.setDate(endD.getDate() - 1)
    this.filterStartTime = this.fmtDate(startD)
    this.filterEndTime = this.fmtDate(endD)
  }

  fmtDate(d: Date): string {
    const y = d.getFullYear()
    const m = String(d.getMonth() + 1).padStart(2, '0')
    const day = String(d.getDate()).padStart(2, '0')
    return `${y}-${m}-${day}`
  }

  tableRowClassName({ row }: { row: ChurnItem }) {
    if (row.is_warning) {
      return 'warning-row'
    }
    return ''
  }

  async handleViewUsers(row: ChurnItem) {
    this.currentNode = row
    this.dialogTitle = `用户对话记录 - ${row.node_name}`
    this.dialogVisible = true
    this.dialogLoading = true
    this.churnUsersData = []
    this.retainedUsers = []
    this.retainedTotal = 0
    this.retainedPage = 1

    await this.loadUsersData()
  }

  async loadUsersData() {
    this.dialogLoading = true

    try {
      const params: any = {
        game_id: this.currentGameId,
        node_id: this.currentNode?.node_id,
        start_date: this.filterStartTime,
        end_date: this.filterEndTime,
        n_days: this.filterNDays,
        retained_page: this.retainedPage,
        retained_page_size: this.retainedPageSize
      }
      if (this.currentNode?.group_node_ids?.length) {
        params.group_node_ids = this.currentNode.group_node_ids.join(',')
      }

      // 设备来源过滤（如果不是"全部"则传递channels参数）
      if (!this.filterChannels.includes('all')) {
        params.channels = this.filterChannels.join(',')
      }

      const response = await service.get('/api/churn_analysis/users', { params })
      if (response.data.message === 'success') {
        this.churnUsersData = response.data.data
        this.retainedUsers = response.data.retained_users || []
        this.retainedTotal = response.data.retained_total || 0
      }
    } catch (error) {
      console.error('获取用户对话失败:', error)
      this.$message.error('获取用户对话失败')
    } finally {
      this.dialogLoading = false
    }
  }

  handleRetainedPageChange(page: number) {
    this.retainedPage = page
    this.loadUsersData()
  }

  // 计算属性
  get warningCount(): number {
    return this.churnData.filter(item => item.is_warning).length
  }

  get averageChurnRate(): number {
    if (this.churnData.length === 0) return 0
    const total = this.churnData.reduce((sum, item) => sum + item.churn_rate, 0)
    return total / this.churnData.length
  }

  get maxChurnNode(): string {
    if (this.churnData.length === 0) return '-'
    const maxItem = this.churnData.reduce((max, item) =>
      item.churn_rate > max.churn_rate ? item : max
    , this.churnData[0])
    return `${maxItem.node_name} (${(maxItem.churn_rate * 100).toFixed(2)}%)`
  }
}
</script>

<style scoped>
.churn-analysis-wrapper {
  padding: 20px;
}

.game-selector {
  margin-bottom: 16px;
}

/* 筛选区域 */
.filter-section {
  margin-bottom: 20px;
}

.filter-content {
  display: flex;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;
}

.filter-item {
  display: flex;
  align-items: center;
  gap: 8px;
}

.filter-label {
  font-size: 14px;
  color: #606266;
  white-space: nowrap;
}

/* 结果表格 */
.result-section {
  margin-bottom: 20px;
}

/* 警告样式 */
.warning-rate {
  color: #f56c6c;
  font-weight: bold;
}

.na-text {
  color: #c0c4cc;
}

.dlg-count-tip {
  margin-left: 6px;
  color: #909399;
  font-size: 12px;
}

/* 警告行样式 - 覆盖所有情况 */
::v-deep .el-table .warning-row,
::v-deep .el-table .warning-row td,
::v-deep .el-table--striped .el-table__body tr.warning-row.el-table__row--striped td,
::v-deep .el-table--striped .el-table__body tr.warning-row td {
  background-color: #fef0f0 !important;
}

::v-deep .el-table .warning-row:hover > td,
::v-deep .el-table--striped .el-table__body tr.warning-row:hover > td {
  background-color: #fde2e2 !important;
}

/* 统计摘要 */
.summary-section {
  margin-top: 20px;
}

.summary-header {
  font-weight: bold;
}

.summary-content {
  display: flex;
  gap: 40px;
  flex-wrap: wrap;
}

.summary-item {
  display: flex;
  align-items: center;
  gap: 8px;
}

.summary-label {
  color: #606266;
  font-size: 14px;
}

.summary-value {
  font-size: 16px;
  font-weight: bold;
  color: #409eff;
}

.summary-value.warning-count {
  color: #f56c6c;
}

/* 弹窗样式 */
.dialog-content {
  max-height: 75vh;
  overflow-y: auto;
}

.no-data {
  text-align: center;
  padding: 60px 0;
  color: #999;
}

.no-data i {
  font-size: 64px;
  margin-bottom: 20px;
}

/* 三栏布局 */
.three-column-layout {
  display: flex;
  gap: 16px;
}

.column {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
}

.column-header {
  background: #f5f7fa;
  padding: 12px 16px;
  border-radius: 8px 8px 0 0;
  border: 1px solid #e4e7ed;
  border-bottom: none;
  display: flex;
  align-items: center;
  gap: 8px;
}

.column-title {
  font-weight: bold;
  color: #303133;
  font-size: 14px;
}

.column-count {
  color: #909399;
  font-size: 12px;
}

.left-column .column-header {
  background: #fef0f0;
  border-color: #fde2e2;
}

.left-column .column-title {
  color: #f56c6c;
}

.middle-column .column-header {
  background: #fdf6ec;
  border-color: #f5dab1;
}

.middle-column .column-title {
  color: #e6a23c;
}

.middle-column .column-content {
  border-color: #f5dab1;
}

.right-column.retained-column .column-header {
  background: #f0f9eb;
  border-color: #e1f3d8;
}

.right-column.retained-column .column-title {
  color: #67c23a;
}

.right-column.retained-column .column-content {
  border-color: #e1f3d8;
}

.column-content {
  border: 1px solid #e4e7ed;
  border-radius: 0 0 8px 8px;
  max-height: 55vh;
  overflow-y: auto;
  padding: 12px;
  background: #fafafa;
  flex: 1;
}

.left-column .column-content {
  border-color: #fde2e2;
}

/* 分页控件 */
.pagination-wrapper {
  padding: 12px;
  text-align: center;
  border: 1px solid #e1f3d8;
  border-top: none;
  border-radius: 0 0 8px 8px;
  background: #fafafa;
}

.empty-column {
  text-align: center;
  color: #909399;
  padding: 40px;
  font-style: italic;
}

/* 用户对话块 */
.user-chat-block {
  border: 1px solid #e4e7ed;
  border-radius: 6px;
  margin-bottom: 12px;
  overflow: hidden;
  background: white;
}

.user-chat-block:last-child {
  margin-bottom: 0;
}

.user-header {
  background: #f5f7fa;
  padding: 8px 12px;
  border-bottom: 1px solid #e4e7ed;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.user-info {
  display: flex;
  align-items: center;
  gap: 8px;
}

.user-id {
  font-size: 11px;
  color: #909399;
  font-family: monospace;
  background: #ecf5ff;
  padding: 2px 6px;
  border-radius: 3px;
}

.user-name {
  font-weight: bold;
  color: #303133;
  font-size: 13px;
}

.user-max-node {
  font-size: 11px;
  color: #606266;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.chat-messages {
  padding: 10px;
  background: white;
}

.no-messages {
  text-align: center;
  color: #909399;
  padding: 15px;
  font-style: italic;
  font-size: 12px;
}

/* 消息项 */
.message-item {
  display: flex;
  margin-bottom: 10px;
  gap: 8px;
}

.message-item:last-child {
  margin-bottom: 0;
}

/* AI消息 - 左侧 */
.ai-message {
  flex-direction: row;
}

/* 用户消息 - 右侧 */
.user-message {
  flex-direction: row-reverse;
}

.message-avatar {
  flex-shrink: 0;
  width: 32px;
  height: 32px;
}

.message-avatar img {
  width: 100%;
  height: 100%;
  border-radius: 50%;
  object-fit: cover;
}

.default-avatar {
  width: 100%;
  height: 100%;
  border-radius: 50%;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: bold;
}

.message-content {
  max-width: 75%;
}

.message-header {
  margin-bottom: 2px;
}

.speaker-name {
  font-size: 11px;
  color: #909399;
}

.send-time {
  font-size: 10px;
  color: #c0c4cc;
  margin-left: 8px;
}

.message-text {
  padding: 8px 10px;
  border-radius: 6px;
  line-height: 1.4;
  word-break: break-word;
  font-size: 13px;
}

.ai-message .message-text {
  background: #f4f4f5;
  border: 1px solid #e4e7ed;
}

.user-message .message-text {
  background: #409eff;
  color: white;
}

.user-message .speaker-name {
  text-align: right;
}

/* 响应式设计 */
@media (max-width: 1400px) {
  .three-column-layout {
    flex-wrap: wrap;
  }

  .three-column-layout .column {
    flex: 1 1 45%;
    min-width: 300px;
  }

  .column-content {
    max-height: 40vh;
  }
}

@media (max-width: 900px) {
  .three-column-layout {
    flex-direction: column;
  }

  .three-column-layout .column {
    flex: 1 1 100%;
  }
}

@media (max-width: 768px) {
  .churn-analysis-wrapper {
    padding: 10px;
  }

  .filter-content {
    flex-direction: column;
    align-items: stretch;
  }

  .filter-item {
    width: 100%;
  }

  .summary-content {
    flex-direction: column;
    gap: 16px;
  }

  .message-content {
    max-width: 85%;
  }
}

.chapter-time-section {
  margin-top: 20px;
}

.chapter-time-header {
  display: flex;
  align-items: baseline;
  gap: 12px;
  flex-wrap: wrap;
}

.chapter-time-title {
  font-weight: 600;
  color: #303133;
}

.chapter-time-tip {
  font-size: 12px;
  color: #909399;
}

.chapter-time-wrap {
  overflow-x: auto;
}

.chapter-time-table {
  border-collapse: collapse;
  width: 100%;
  font-size: 13px;
}

.chapter-time-table th,
.chapter-time-table td {
  border: 1px solid #ebeef5;
  padding: 8px 12px;
  text-align: center;
  white-space: nowrap;
}

.chapter-time-table thead th {
  background-color: #f5f7fa;
  color: #606266;
  font-weight: 600;
}

.chapter-time-table .ct-rowhead {
  background-color: #f5f7fa;
  color: #606266;
  font-weight: 600;
  position: sticky;
  left: 0;
  z-index: 1;
}
</style>
